// 카피바라 몸 효과: 더우면 홍조와 땀방울, 추우면 하얀 입김 (떨림은 pose.ts).
// 프로토타입 3552–3577. 덥고 추운 조건(어느 방·자리인지)은 방 모듈이 정해서 넘긴다.
import * as THREE from 'three';
import { S } from '../engine/geometry';
import { steamTex } from '../engine/textures';
import type { Capy } from './agent';

export interface BodyEnv {
  /** 황토방·소금방·열탕 같은 더운 곳. 매운 음식은 여기서 따로 본다. */
  hot: boolean;
  /** 얼음방·냉탕 같은 추운 곳. */
  cold: boolean;
}

interface BodyFx { cheeks: THREE.Mesh[]; drops: THREE.Mesh[]; breath: THREE.Sprite }

const blushMat = () => new THREE.MeshBasicMaterial({ color: 0xFF6F86, transparent: true, opacity: 0, depthWrite: false });
const SWEAT_MAT = new THREE.MeshStandardMaterial({ color: 0xCFEFFF, roughness: 0.05, metalness: 0.1, transparent: true, opacity: 0.85, emissive: 0x9FD8FF, emissiveIntensity: 0.25 });
const fxOf = new WeakMap<Capy, BodyFx>();
const vT = new THREE.Vector3();

function ensureFx(a: Capy, world: THREE.Object3D): BodyFx {
  const had = fxOf.get(a);
  if (had) return had;
  const c = a.c, f: BodyFx = { cheeks: [], drops: [], breath: new THREE.Sprite(new THREE.SpriteMaterial({ map: steamTex(), transparent: true, depthWrite: false, opacity: 0 })) };
  [1, -1].forEach((s) => {
    const m = new THREE.Mesh(new THREE.CircleGeometry(0.15, 18), blushMat());
    m.position.set(s * 0.5, -0.06, 0.78); m.rotation.y = s * 0.65; m.scale.set(1, 0.65, 1);
    c.headG.add(m); f.cheeks.push(m);
  });
  ([[0.3, 0.42, 0.86], [-0.36, 0.38, 0.84], [0.6, 0.15, 0.5], [-0.6, 0.12, 0.5], [0.15, 0.55, 0.78]] as const).forEach(([x, y, z], i) => {
    const d = new THREE.Mesh(S(0.045, 10, 8), SWEAT_MAT);
    d.scale.set(0.75, 1.25, 0.75); d.userData = { x, y, z, ph: i * 0.23 }; d.visible = false;
    c.headG.add(d); f.drops.push(d);
  });
  world.add(f.breath);
  fxOf.set(a, f);
  return f;
}

/** world: 입김 스프라이트를 붙일 방의 루트. */
export function bodyFxTick(a: Capy, t: number, dt: number, env: BodyEnv, world: THREE.Object3D): void {
  const spicy = (a.spicyUntil || 0) > t, hot = env.hot || spicy, cold = env.cold;
  a.heat = Math.max(0, Math.min(1, (a.heat || 0) + (hot ? dt / (spicy ? 3 : 18) : -dt / (cold ? 5 : 25))));
  a.cold = Math.max(0, Math.min(1, (a.cold || 0) + (cold ? dt / 6 : -dt / 8)));
  if (!a.c.root.visible) return;
  if (a.heat < 0.02 && a.cold < 0.02 && !fxOf.has(a)) return;
  const f = ensureFx(a, world);
  f.cheeks.forEach((m) => { (m.material as THREE.MeshBasicMaterial).opacity = Math.min(0.6, a.heat! * 0.7); });
  const sw = Math.max(0, (a.heat - 0.3) / 0.7);
  f.drops.forEach((d, i) => {
    const u = d.userData as { x: number; y: number; z: number; ph: number }, q = (t * 0.35 + u.ph) % 1;
    d.visible = sw > 0.05 && i < 2 + Math.round(sw * 3);
    d.position.set(u.x, u.y - q * 0.45, u.z);
    d.scale.set(0.75 * (0.6 + sw * 0.6), 1.25 * (0.6 + sw * 0.6), 0.75 * (0.6 + sw * 0.6));
  });
  const bq = (t * 0.42 + a.seedP) % 1, bm = f.breath.material;
  if (a.cold > 0.35) {
    a.c.root.updateMatrixWorld(true);
    a.c.headG.localToWorld(vT.set(0, -0.3, 1.3 + bq * 0.5));
    f.breath.position.copy(vT);
    const sc = (0.25 + bq * 0.6) * (a.size || 1);
    f.breath.scale.set(sc, sc, 1);
    bm.opacity = Math.sin(bq * Math.PI) * 0.55 * a.cold;
  } else bm.opacity = 0;
}

/** 다른 구역에 있어 안 보이는 카피바라의 입김도 숨긴다 (찜질방). */
export function setBreathVisible(a: Capy, vis: boolean): void {
  const f = fxOf.get(a);
  if (f) f.breath.visible = vis;
}
